import { isObject } from '@app/lib/common'
import type { AuthenticatedRequest } from '@app/rest/rest.lib';
import { authenticatedRequest } from '@app/rest/rest.lib'

import type {
  MedicationDosageUnit,
  MedicationFrequencyUnit,
  MedicationRoute,
  PatientMedicationComments,
  PatientMedicationDosage,
  PatientMedicationFrequency,
  PatientMedicationFrequencyUnitId,
  PatientMedicationHistory,
  PatientMedicationId,
  PatientMedicationIndications,
  PatientMedicationPrescriber,
  PatientMedicationRecommendations,
  PatientMedicationRecordEntryDefault,
  PatientMedicationRecordEntryStatus,
  PatientMedicationRecordId,
} from './patient-medication-history.interface'
import type { IPatientId } from '../patient/patient.interface'


export const enum ResponseStatus {
  Error = 'error',
  Success = 'success',
}

export function isMedicationError(
  x: unknown,
): x is IPatientMedicationHistoryErrorResponse {
  return isObject(x) && x.status === 'error'
}

export enum ErrorType {
  PatientMedicationConflictsWithExisting = 'patient-medication-conflicts-with-existing',
  PatientMedicationNotFound = 'patient-medication-not-found',
  PatientMedicationRecordConflictsWithExisting = 'patient-medication-record-conflicts-with-existing',
  PatientMedicationRecordNotFound = 'patient-medication-record-not-found',
  ValidationError = 'validation-error',
}

export enum ErrorMessages {
  PatientMedicationConflictsWithExisting = 'Patient medication already exists.',
  PatientMedicationNotFound = 'Patient medication not found.',
  PatientMedicationRecordConflictsWithExisting = 'Patient medication record already exists.',
  PatientMedicationRecordNotFound = 'Patient medication record not found.',
  ValidationError = 'A validation error ocurred. Please check your form inputs.',
  unspecifiedError = 'An error occured. Please try again.',
}

type IPatientMedicationHistorySuccessResponse = {
  status: ResponseStatus.Success
  patientMedicationHistory: PatientMedicationHistory
}

export const enum ValidationErrorType {
  Invalid = 'invalid',
  Required = 'required',
}

export interface ValidationError {
  field: string
  error: ValidationErrorType
}

export type IPatientMedicationHistoryErrorResponse =
  | {
      status: ResponseStatus.Error
      errorType: ErrorType.PatientMedicationConflictsWithExisting
    }
  | {
      status: ResponseStatus.Error
      errorType: ErrorType.PatientMedicationNotFound
    }
  | {
      status: ResponseStatus.Error
      errorType: ErrorType.PatientMedicationRecordConflictsWithExisting
    }
  | {
      status: ResponseStatus.Error
      errorType: ErrorType.PatientMedicationRecordNotFound
    }
  | {
      status: ResponseStatus.Error
      errorType: ErrorType.ValidationError
      validationErrors: ValidationError[]
    }

export type IPatientMedicationHistoryResponse =
  | IPatientMedicationHistorySuccessResponse
  | IPatientMedicationHistoryErrorResponse

export function getPatientMedicationHistory(
  patientId: IPatientId,
): AuthenticatedRequest<IPatientMedicationHistoryResponse> {
  const url = `/api/v2/patient/${patientId}/medication-history`

  return authenticatedRequest<IPatientMedicationHistoryResponse>({
    method: 'get',
    url,
  })
}

export type IGetMedicationDosageUnitsResponse = {
  medicationDosageUnits: MedicationDosageUnit[]
}

export function getMedicationDosageUnits(): AuthenticatedRequest<
  IGetMedicationDosageUnitsResponse
> {
  const url = `/api/v2/medication-dosage-units`

  return authenticatedRequest<IGetMedicationDosageUnitsResponse>({
    method: 'get',
    url,
  })
}

export type IGetMedicationFrequencyUnitsResponse = {
  medicationFrequencyUnits: MedicationFrequencyUnit[]
}

export function getMedicationFrequencyUnits(): AuthenticatedRequest<
  IGetMedicationFrequencyUnitsResponse
> {
  const url = `/api/v2/medication-frequency-units`

  return authenticatedRequest<IGetMedicationFrequencyUnitsResponse>({
    method: 'get',
    url,
  })
}

export type IGetMedicationRoutesResponse = {
  medicationRoutes: MedicationRoute[]
}

export function getMedicationRoutes(): AuthenticatedRequest<
  IGetMedicationRoutesResponse
> {
  const url = `/api/v2/medication-routes`

  return authenticatedRequest<IGetMedicationRoutesResponse>({
    method: 'get',
    url,
  })
}

export function addMedicationToPatientMedicationHistory(
  patientId: IPatientId,
  medicationDosageUnitId: string,
  medicationRouteId: string,
  name: string,
): AuthenticatedRequest<IPatientMedicationHistoryResponse> {
  const url = `/api/v2/patient/${patientId}/medication-history/add-medication`

  return authenticatedRequest<IPatientMedicationHistoryResponse>({
    method: 'post',
    url,
    data: {
      medicationDosageUnitId,
      medicationRouteId,
      name,
    },
  })
}

export function deleteMedicationOnPatientMedicationHistory(
  patientId: IPatientId,
  patientMedicationId: PatientMedicationId,
): AuthenticatedRequest<IPatientMedicationHistoryResponse> {
  const url = `/api/v2/patient/${patientId}/medication-history/delete-medication`

  return authenticatedRequest<IPatientMedicationHistoryResponse>({
    method: 'post',
    url,
    data: {
      patientMedicationId,
    },
  })
}

export function updateMedicationOnPatientMedicationHistory(
  patientId: IPatientId,
  patientMedicationId: PatientMedicationId,
  medicationDosageUnitId: string,
  medicationRouteId: string,
  name: string,
): AuthenticatedRequest<IPatientMedicationHistoryResponse> {
  const url = `/api/v2/patient/${patientId}/medication-history/update-medication`

  return authenticatedRequest<IPatientMedicationHistoryResponse>({
    method: 'post',
    url,
    data: {
      medicationDosageUnitId,
      medicationRouteId,
      name,
      patientMedicationId,
    },
  })
}

export function addMedicationRecordToPatientMedicationHistory(
  patientId: IPatientId,
  activeDate: string,
): AuthenticatedRequest<IPatientMedicationHistoryResponse> {
  const url = `/api/v2/patient/${patientId}/medication-history/add-medication-record`

  return authenticatedRequest<IPatientMedicationHistoryResponse>({
    method: 'post',
    url,
    data: {
      activeDate,
    },
  })
}

export function deleteMedicationRecordOnPatientMedicationHistory(
  patientId: IPatientId,
  patientMedicationRecordId: PatientMedicationRecordId,
): AuthenticatedRequest<IPatientMedicationHistoryResponse> {
  const url = `/api/v2/patient/${patientId}/medication-history/delete-medication-record`

  return authenticatedRequest<IPatientMedicationHistoryResponse>({
    method: 'post',
    url,
    data: {
      patientMedicationRecordId,
    },
  })
}

export function updateMedicationRecordOnPatientMedicationHistory(
  patientId: IPatientId,
  patientMedicationRecordId: PatientMedicationRecordId,
  activeDate: string,
): AuthenticatedRequest<IPatientMedicationHistoryResponse> {
  const url = `/api/v2/patient/${patientId}/medication-history/update-medication-record`

  return authenticatedRequest<IPatientMedicationHistoryResponse>({
    method: 'post',
    url,
    data: {
      activeDate,
      patientMedicationRecordId,
    },
  })
}

export function addOrUpdateMedicationRecordEntryOnPatientMedicationHistory(
  patientId: IPatientId,
  patientMedicationId: PatientMedicationId,
  patientMedicationRecordId: PatientMedicationRecordId,
  status: PatientMedicationRecordEntryStatus,
  dosage: PatientMedicationDosage,
  frequency: PatientMedicationFrequency,
  frequencyUnitId: PatientMedicationFrequencyUnitId,
  indications?: PatientMedicationIndications,
  prescriber?: PatientMedicationPrescriber,
  comments?: PatientMedicationComments,
  recommendations?: PatientMedicationRecommendations,
): AuthenticatedRequest<IPatientMedicationHistoryResponse> {
  const url = `/api/v2/patient/${patientId}/medication-history/medication-record-entry`

  return authenticatedRequest<IPatientMedicationHistoryResponse>({
    method: 'post',
    url,
    data: {
      patientMedicationId,
      patientMedicationRecordId,
      status,
      dosage,
      frequency,
      frequencyUnitId,
      indications,
      prescriber,
      comments,
      recommendations,
    },
  })
}

export type IGetMedicationRecordEntryDefaultsResponse = {
  defaults: PatientMedicationRecordEntryDefault
}

export function getMedicationRecordEntryDefaults(
  patientId: IPatientId,
  patientMedicationId: PatientMedicationId,
  patientMedicationRecordId: PatientMedicationRecordId,
): AuthenticatedRequest<IGetMedicationRecordEntryDefaultsResponse> {
  const paramArray: string[][] = [
    ['medicationId', patientMedicationId],
    ['recordId', patientMedicationRecordId],
  ].filter((it) => {
    return it[1] !== undefined && it[1] !== null
  })

  const urlParams = new URLSearchParams(paramArray).toString()

  const url = `/api/v2/patient/${patientId}/medication-history/get-medication-record-entry-defaults?${urlParams}`

  return authenticatedRequest<IGetMedicationRecordEntryDefaultsResponse>({
    method: 'get',
    url,
  })
}
